import { useState, Fragment } from 'react';
import { classNames } from 'primereact/utils';
import { SidebarMenuItem } from '.';
import { SidebarMenuProps } from '../../../models/sidebar/sidebar';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { Tenant } from '../../../models/tenant';

const SidebarMenu = (props: { className?: string; minimized: boolean }): JSX.Element => {
  const { className, minimized } = props;

  const [currentMenu, setCurrentMenu] = useState<string>('');

  const { user, tenant: authTenant } = useAuth();
  const tenant = authTenant as unknown as Tenant | undefined;

  const selectMenu = (id: string): void => {
    if (currentMenu === id) {
      setCurrentMenu('');
    } else {
      setCurrentMenu(id);
    }
  };

  const isAuthorized = (authorizedGroups?: Array<string>) => {
    if (!authorizedGroups) {
      return true;
    }
    const userGroups: string[] = user?.roles || [];
    return userGroups && authorizedGroups.some((g) => userGroups.includes(g));
  };

  const isProductEnabled = (label: string): boolean => {
    if (!tenant?.enabledProducts) return true;
    const productMap: Record<string, boolean | undefined> = {
      'Credit': tenant.enabledProducts.credit,
      'VoX': tenant.enabledProducts.vox,
      'Fees': tenant.enabledProducts.fees,
      'Price': tenant.enabledProducts.pricing,
      'AUW': tenant.enabledProducts.automatedUnderwrite,
    };
    return productMap[label] ?? true;
  };

  const content = [
    {
      header: 'Overview',
      menuItems: [
        {
          label: 'Dashboard',
          to: '/dashboard',
          icon: 'pi-th-large',
        },
        {
          label: 'Waterfalls',
          to: '/waterfalls',
          icon: 'pi-sitemap',
          authorizedGroups: ['SuperAdmin', 'Admin'],
        },
      ],
    },
    {
      header: 'Products',
      menuItems: [
        {
          label: 'Credit',
          icon: 'pi-credit-card',
          to: '/products/credit',
          submenuItems: [
            {
              label: 'Emulator',
              to: '/credit/emulator',
            },
            {
              label: 'Accounts',
              to: '/credit/accounts',
            },
            {
              label: 'Transactions',
              to: '/credit/transactions',
            },
            {
              label: 'Reporting Agencies',
              to: '/credit-reporting-agencies',
            },
            {
              label: 'Waterfalls',
              to: '/credit/waterfalls',
            },
          ],
        },
        {
          label: 'AUW',
          icon: 'pi-shield',
          to: '/products/auw',
          submenuItems: [
            {
              label: 'Applications',
              to: '/underwrite',
            },
            {
              label: 'Waterfalls',
              to: '/auw/waterfalls',
            },
          ],
        },
        {
          label: 'Fees',
          icon: 'pi-dollar',
          to: '/products/fees',
          submenuItems: [
            {
              label: 'Transaction Logs',
              to: '/fee-tracing',
            },
            {
              label: 'Fee Configurations',
              to: '/fees',
            },
            {
              label: 'HUD Lines',
              to: '/fees/hud-lines',
            },
            {
              label: 'Waterfalls',
              to: '/fees/waterfalls',
            },
          ],
        },
        {
          label: 'VoX',
          icon: 'pi-bolt',
          to: '/products/vox',
          submenuItems: [
            {
              label: 'Transaction Logs',
              to: '/vox-tracing',
            },
            {
              label: 'Waterfalls',
              to: '/vox/waterfalls',
            },
          ],
        },
        {
          label: 'Price',
          icon: 'pi-tag',
          to: '/products/price',
          submenuItems: [
            {
              label: 'Coming Soon',
              to: '#',
            },
          ],
        },
      ],
    },
    {
      header: 'Operations',
      menuItems: [
        {
          label: 'Billing',
          to: '/billing',
          icon: 'pi-wallet',
        },
      ],
    },
    {
      header: 'Administration',
      authorizedGroups: ['SuperAdmin', 'Admin'],
      menuItems: [
        {
          label: 'Tenants',
          to: '/tenants',
          icon: 'pi-building',
          authorizedGroups: ['SuperAdmin'],
        },
        {
          label: 'User Accounts',
          to: '/user-accounts',
          icon: 'pi-users',
          authorizedGroups: ['SuperAdmin', 'Admin'],
        },
        {
          label: 'Connection Settings',
          to: '/connection-settings',
          icon: 'pi-cog',
          authorizedGroups: ['SuperAdmin', 'Admin'],
        },
        {
          label: 'Webhook Subscriptions',
          to: '/webhook-subscriptions',
          icon: 'pi-link',
          authorizedGroups: ['SuperAdmin', 'Admin'],
        },
        {
          label: 'Custom Fields',
          to: '/custom-fields',
          icon: 'pi-sliders-h',
          authorizedGroups: ['SuperAdmin', 'Admin'],
        },
      ],
    },
    {
      header: 'Super Admin',
      authorizedGroups: ['SuperAdmin'],
      menuItems: [
        {
          label: 'Status Incidents',
          to: '/status-incidents',
          icon: 'pi-exclamation-triangle',
          authorizedGroups: ['SuperAdmin'],
        },
      ],
    },
  ];

  return (
    <section className={classNames('overflow-y-auto nav-scrollbar p-4', className)}>
      {content.map((ele, idx) => {
        if (!isAuthorized(ele.authorizedGroups)) return null;

        const section = ele.menuItems;
        const sectionIdx = idx;
        return (
          <Fragment key={idx}>
            {ele.header && (
              <div className="overflow-x-hidden mt-5 mb-2 first:mt-0">
                {!minimized ? (
                  <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--sidebar-menu-heading-color)]">
                    {ele.header}
                  </span>
                ) : (
                  <div className="mx-auto w-5 border-t border-[var(--sidebar-separator-color)]" />
                )}
              </div>
            )}
            <>
              {section.map((ele: SidebarMenuProps, idx) => {
                if (ele.authorizedGroups && !isAuthorized(ele.authorizedGroups)) {
                  return null;
                }
                if (sectionIdx === 1 && !isProductEnabled(ele.label)) {
                  return null;
                }

                return (
                  <SidebarMenuItem
                    key={idx}
                    id={`${sectionIdx}_${idx}`}
                    minimized={minimized}
                    label={ele.label}
                    to={ele.to}
                    submenuItems={ele.submenuItems}
                    icon={ele.icon}
                    currentMenu={currentMenu}
                    selectMenu={selectMenu}
                  />
                );
              })}
            </>
          </Fragment>
        );
      })}
    </section>
  );
};

export default SidebarMenu;
